import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    /*
     * IMPORTANT:
     * Android Chrome can dismiss a native <dialog> using the browser
     * back button. If we allow the browser to close it directly,
     * closeDialog() does not run and the page remains scroll-locked.
     *
     * Catch the native cancel event and close it through our own
     * closeDialog() method instead.
     */
    this.refs.dialog.addEventListener('cancel', this.#handleNativeCancel);

    /*
     * Extra safety:
     * If the browser/native dialog gets closed without closeDialog(),
     * remove any leftover scroll lock.
     */
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog.removeEventListener('cancel', this.#handleNativeCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Handles native dialog cancel events.
   * This is especially important for Android browser Back.
   *
   * @param {Event} event
   */
  #handleNativeCancel = (event) => {
    /*
     * Stop the browser from directly closing the <dialog>.
     * We need closeDialog() to run so scroll locking is cleaned up.
     */
    event.preventDefault();

    this.closeDialog();
  };

  /**
   * Fallback cleanup if the native dialog somehow closes without
   * going through closeDialog().
   */
  #handleNativeClose = () => {
    /*
     * Normally closeDialog() has already removed these.
     * This only acts as a safety fallback.
     */
    if (!document.documentElement.hasAttribute('scroll-lock')) return;

    document.documentElement.removeAttribute('scroll-lock');

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.top = '';

    window.scrollTo(0, this.#previousScrollY);
    document.documentElement.scrollTop = this.#previousScrollY;
    document.body.scrollTop = this.#previousScrollY;
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;

    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      const restore = () => {
        window.scrollTo(0, scrollY);
        document.documentElement.scrollTop = scrollY;
        document.body.scrollTop = scrollY;
      };

      restore();
      setTimeout(restore, 0);
      setTimeout(restore, 50);

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      /*
       * Safety cleanup.
       *
       * In case a browser has already closed the dialog but left the
       * page locked, make sure we still restore scrolling.
       */
      if (document.documentElement.hasAttribute('scroll-lock')) {
        document.documentElement.removeAttribute('scroll-lock');

        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.top = '';

        window.scrollTo(0, this.#previousScrollY);
        document.documentElement.scrollTop = this.#previousScrollY;
        document.body.scrollTop = this.#previousScrollY;
      }

      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.documentElement.removeAttribute('scroll-lock');

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.top = '';

    window.scrollTo(0, this.#previousScrollY);
    document.documentElement.scrollTop = this.#previousScrollY;
    document.body.scrollTop = this.#previousScrollY;

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);